import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { adminAPI } from '../../services/adminApi';
import ConfirmModal from '../shared/ConfirmModal';
import { useToast } from '../shared/Toast';
import { LoadFailed, SwitchRow } from '../settings/instantSettings';
import { SheetField, SheetLocked } from '../settings/sheet';
import {
  Column, DANGER_GHOST_BTN, DataTable, DetailPanel, EmptyState, FilterSelect, GHOST_BTN, INPUT_CLS, ListPage, ListToolbar,
  PRIMARY_BTN, SELECT_CLS, StatusChip, matchesSearch,
} from '../shared/listPage';

/**
 * Super admin «Συνδέσεις πλατφορμών» (e-food / Wolt / BOX ανά κατάστημα) στη διάταξη «Πίνακας» (14/10/2026):
 * αναζήτηση + φίλτρο πλατφόρμας, μία γραμμή ανά σύνδεση, συρτάρι με τη φόρμα (νέα / αλλαγή / διαγραφή).
 * Ίδια endpoints και ίδιο σώμα με πριν: GET /api/admin/platform-credentials + GET /api/admin/stores,
 * PUT /api/admin/platform-credentials/:storeId/:platform (χωρίς store_id/platform_name στο σώμα, κωδικός
 * και κλειδί μόνο όταν γράφτηκαν — κενό = μένει το αποθηκευμένο), DELETE στην ίδια διαδρομή.
 */

interface PlatformCred {
  id: number;
  store_id: number;
  store_name: string;
  platform_name: string;
  api_username: string | null;
  restaurant_id: string | null;
  is_self_delivery: boolean;
  auto_accept_orders: boolean;
  est_delivery_time: number | null;
  preset_times: any;
  created_at: string;
}

interface StoreOption {
  id: number;
  name: string;
  display_name?: string | null;
  organization_name?: string | null;
}

interface CredForm {
  store_id: number;
  platform_name: string;
  api_username: string;
  api_password: string;
  api_key: string;
  restaurant_id: string;
  is_self_delivery: boolean;
  auto_accept_orders: boolean;
  est_delivery_time: number;
}

type LoadState = 'ready' | 'error' | 'forbidden';

// Ονόματα πλατφορμών όπως τα γράφουν οι ίδιες (εμπορικά ονόματα, ίδια σε κάθε γλώσσα).
const PLATFORM_LABELS: Record<string, string> = { efood: 'e-food', wolt: 'Wolt', box: 'BOX' };
const PLATFORMS = ['efood', 'wolt', 'box'];
const platformLabel = (name: string) => PLATFORM_LABELS[name] || name;

const FORM_ID = 'sa-platform-form';

const SuperAdminPlatformsTab: React.FC = () => {
  const { t } = useTranslation(['superadminOps', 'admin', 'common', 'listPage']);
  const { showToast } = useToast();
  const [credentials, setCredentials] = useState<PlatformCred[]>([]);
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<LoadState>('ready');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');

  // Συρτάρι νέας/υπάρχουσας σύνδεσης
  const [panelOpen, setPanelOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingCred, setEditingCred] = useState<PlatformCred | null>(null);
  const [formData, setFormData] = useState<CredForm | null>(null);
  const [initialForm, setInitialForm] = useState<CredForm | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [storeMissing, setStoreMissing] = useState(false);
  const [confirm, setConfirm] = useState<'delete' | 'discard' | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [credData, storeData] = await Promise.all([
        adminAPI.getPlatformCredentials(),
        adminAPI.getStores(),
      ]);
      setCredentials((credData as any) || []);
      setStores(((storeData as any) || []).map((s: any) => ({
        id: s.id, name: s.name, display_name: s.display_name, organization_name: s.organization_name,
      })));
      setState('ready');
    } catch (err: any) {
      console.error('Failed to load platform credentials:', err);
      setState(err?.response?.status === 403 ? 'forbidden' : 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Εμφανίζουμε display_name (+ όνομα οργανισμού), όπως τα άλλα tabs — όχι την επίσημη επωνυμία. Τα
  // credentials φέρνουν μόνο store_name (επωνυμία), οπότε αντλούμε display/org από τα πλήρη store objects.
  const storeById = useMemo(() => {
    const m: Record<number, StoreOption> = {};
    for (const s of stores) m[s.id] = s;
    return m;
  }, [stores]);

  const storeLabel = useCallback((storeId: number, fallback?: string) => {
    const s = storeById[storeId];
    const base = s?.display_name || s?.name || fallback || `#${storeId}`;
    return s?.organization_name ? `${base} (${s.organization_name})` : base;
  }, [storeById]);

  const filtered = credentials.filter(c => {
    const s = storeById[c.store_id];
    const matchSearch = matchesSearch(searchTerm, c.store_name, s?.display_name, s?.organization_name, c.platform_name,
      platformLabel(c.platform_name), c.restaurant_id);
    const matchPlatform = !filterPlatform || c.platform_name === filterPlatform;
    return matchSearch && matchPlatform;
  });
  const filteredStores = new Set(filtered.map(c => c.store_id)).size;
  const uniquePlatforms = Array.from(new Set(credentials.map(c => c.platform_name))).sort();

  const openCreate = () => {
    const next: CredForm = {
      store_id: stores.length > 0 ? stores[0].id : 0,
      platform_name: 'efood',
      api_username: '', api_password: '', api_key: '', restaurant_id: '',
      is_self_delivery: false, auto_accept_orders: false, est_delivery_time: 45,
    };
    setEditingCred(null);
    setFormData(next);
    setInitialForm(next);
    setFormError(null);
    setStoreMissing(false);
    setPanelOpen(true);
  };

  const openEdit = (cred: PlatformCred) => {
    const next: CredForm = {
      store_id: cred.store_id,
      platform_name: cred.platform_name,
      api_username: cred.api_username || '',
      api_password: '',
      api_key: '',
      restaurant_id: cred.restaurant_id || '',
      is_self_delivery: cred.is_self_delivery,
      auto_accept_orders: cred.auto_accept_orders,
      est_delivery_time: cred.est_delivery_time || 45,
    };
    setEditingCred(cred);
    setFormData(next);
    setInitialForm(next);
    setFormError(null);
    setStoreMissing(false);
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
    setConfirm(null);
    setEditingCred(null);
    setFormData(null);
    setInitialForm(null);
    setFormError(null);
  };

  const dirty = !!formData && !!initialForm && JSON.stringify(formData) !== JSON.stringify(initialForm);

  // Esc / φόντο / X: πρώτα κλείνει μια ανοιχτή ερώτηση· με αλλαγές που δεν αποθηκεύτηκαν, ρωτά.
  const requestClose = () => {
    if (saving) return;
    if (confirm) { setConfirm(null); return; }
    if (dirty) { setConfirm('discard'); return; }
    closePanel();
  };

  const change = <K extends keyof CredForm>(key: K, value: CredForm[K]) => {
    setFormError(null);
    if (key === 'store_id') setStoreMissing(false);
    setFormData(prev => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!formData || saving) return;
    if (!formData.store_id) { setStoreMissing(true); return; }
    try {
      setSaving(true);
      setFormError(null);
      const payload: any = { ...formData };
      delete payload.store_id;
      delete payload.platform_name;
      // Μόνο ό,τι γράφτηκε: κενός κωδικός/κλειδί = μένει το αποθηκευμένο.
      if (!payload.api_password) delete payload.api_password;
      if (!payload.api_key) delete payload.api_key;

      await adminAPI.updatePlatformCredentials(formData.store_id, formData.platform_name, payload);
      closePanel();
      await fetchAll();
    } catch (err: any) {
      setFormError(err.response?.data?.error || t('admin:failedToSaveCredentials'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cred: PlatformCred) => {
    try {
      await adminAPI.deletePlatformCredentials(cred.store_id, cred.platform_name);
      closePanel();
      await fetchAll();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedToDeleteCredentials'));
    }
  };

  const columns = useMemo<Column<PlatformCred>[]>(() => {
    // «Ναι» της αυτόματης αποδοχής πράσινο (ενεργό)· της δικής μας διανομής ουδέτερο (απλώς ιδιότητα).
    const yesNo = (on: boolean, tone: 'ok' | 'brand' = 'ok') => (on
      ? <StatusChip tone={tone}>{t('superadminOps:yes')}</StatusChip>
      : <span className="text-t4">—</span>);
    // Επικεφαλίδα που αναδιπλώνεται σε δύο γραμμές αντί να κοπεί με «…».
    const wrapHead = (text: string) => <span className="block whitespace-normal leading-4">{text}</span>;
    return [
      {
        key: 'store',
        header: t('superadminOps:platforms.col.store'),
        width: 'minmax(180px, 2fr)',
        // Όνομα καταστήματος από πάνω, οργανισμός και αριθμός από κάτω (όπως η Τιμολόγηση).
        render: c => {
          const s = storeById[c.store_id];
          return (
            <>
              <span className="block font-medium">{s?.display_name || s?.name || c.store_name}</span>
              <span className="block text-xs text-t3">{[s?.organization_name, `#${c.store_id}`].filter(Boolean).join(' · ')}</span>
            </>
          );
        },
        // Στο κινητό μόνο το όνομα (ο οργανισμός στο συρτάρι): με παρένθεση κοβόταν.
        stackRender: c => storeById[c.store_id]?.display_name || storeById[c.store_id]?.name || c.store_name,
      },
      {
        key: 'platform_name',
        header: t('superadminOps:platforms.col.platform'),
        width: '104px',
        stack: 'end',
        render: c => <span className="font-semibold">{platformLabel(c.platform_name)}</span>,
        stackRender: c => platformLabel(c.platform_name),
      },
      {
        key: 'api_username',
        header: t('superadminOps:platforms.col.username'),
        width: 'minmax(130px, 1.2fr)',
        hideBelow: 'medium',
        render: c => (c.api_username ? <span className="text-t2 break-all">{c.api_username}</span> : <span className="text-t4">—</span>),
      },
      {
        key: 'restaurant_id',
        header: wrapHead(t('superadminOps:platforms.col.restaurant')),
        width: 'minmax(120px, 1fr)',
        render: c => (c.restaurant_id ? <span className="font-mono text-xs text-t2 break-all">{c.restaurant_id}</span> : <span className="text-t4">—</span>),
        stackRender: c => (c.restaurant_id ? `ID ${c.restaurant_id}` : ''),
      },
      {
        key: 'auto_accept_orders',
        header: wrapHead(t('superadminOps:platforms.col.autoAccept')),
        width: '112px',
        render: c => yesNo(c.auto_accept_orders),
        stackRender: c => (c.auto_accept_orders ? t('superadminOps:platforms.autoAcceptShort') : ''),
      },
      {
        key: 'is_self_delivery',
        header: wrapHead(t('superadminOps:platforms.col.selfDelivery')),
        width: '104px',
        hideBelow: 'medium',
        render: c => yesNo(c.is_self_delivery, 'brand'),
      },
      {
        key: 'est_delivery_time',
        header: t('superadminOps:platforms.col.time'),
        width: '84px',
        align: 'right',
        render: c => (c.est_delivery_time ? <span className="tabular-nums text-t2">{c.est_delivery_time}′</span> : <span className="text-t4">—</span>),
        stackRender: c => (c.est_delivery_time ? `${c.est_delivery_time}′` : ''),
      },
    ];
  }, [t, storeLabel, storeById]);

  const platformOptions = uniquePlatforms.map(p => ({
    value: p,
    label: t('superadminOps:platforms.platformOption', { platform: platformLabel(p), count: credentials.filter(c => c.platform_name === p).length }),
  }));

  const filtersActive = searchTerm.trim() !== '' || filterPlatform !== '';
  const firstLoad = loading && credentials.length === 0;
  const isEdit = editingCred !== null;

  const confirmModal = (
    <ConfirmModal
      isOpen={confirm !== null}
      variant={confirm === 'delete' ? 'danger' : 'warning'}
      title={confirm === 'delete' ? t('superadminOps:platforms.deleteTitle') : t('superadminOps:discard.title')}
      message={confirm === 'delete' && editingCred
        ? t('superadminOps:platforms.deleteConfirm', {
          platform: platformLabel(editingCred.platform_name),
          store: storeLabel(editingCred.store_id, editingCred.store_name),
        })
        : t('superadminOps:discard.message')}
      confirmLabel={confirm === 'delete' ? t('superadminOps:delete') : t('superadminOps:discard.confirm')}
      cancelLabel={confirm === 'delete' ? t('superadminOps:back') : t('superadminOps:discard.keep')}
      onConfirm={() => {
        if (confirm === 'delete' && editingCred) {
          setConfirm(null);
          handleDelete(editingCred);
        } else {
          closePanel();
        }
      }}
      onCancel={() => setConfirm(null)}
    />
  );

  return (
    <ListPage
      eyebrow={t('superAdminPanel:nav.groups.connections')}
      title={t('superadminOps:platforms.title')}
      subtitle={t('superadminOps:platforms.subtitle')}
      testId="sa-platforms"
    >
      <ListToolbar
        search={{
          value: searchTerm,
          onChange: setSearchTerm,
          placeholder: t('superadminOps:platforms.searchPlaceholder'),
          ariaLabel: t('superadminOps:platforms.searchLabel'),
        }}
        filters={(
          <>
            <FilterSelect
              label={t('superadminOps:platforms.platformFilter')}
              value={filterPlatform}
              onChange={setFilterPlatform}
              allLabel={t('superadminOps:platforms.allPlatforms', { count: credentials.length })}
              options={platformOptions}
              testId="sa-platforms-filter"
            />
            {filtersActive && (
              <button type="button" className={GHOST_BTN} onClick={() => { setSearchTerm(''); setFilterPlatform(''); }}>
                {t('common:clear')}
              </button>
            )}
          </>
        )}
        actions={(
          <>
            <button type="button" className={GHOST_BTN} onClick={() => fetchAll()} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
              {t('superadminOps:refresh')}
            </button>
            <button type="button" className={PRIMARY_BTN} onClick={openCreate} disabled={state !== 'ready' || firstLoad}>
              <Plus className="w-4 h-4" aria-hidden="true" />
              {t('superadminOps:platforms.new')}
            </button>
          </>
        )}
        summary={state === 'ready' && !firstLoad
          ? t('superadminOps:platforms.count', { count: filtered.length, stores: filteredStores })
          : null}
      />

      {state !== 'ready' ? (
        <LoadFailed
          message={state === 'forbidden' ? t('superadminOps:forbidden') : t('superadminOps:platforms.loadFailed')}
          onRetry={() => fetchAll()}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={c => c.id}
          onRowClick={openEdit}
          selectedKey={panelOpen && editingCred ? editingCred.id : null}
          loading={loading}
          stickyHeader
          rowLabel={c => t('superadminOps:platforms.rowLabel', { platform: platformLabel(c.platform_name), store: storeLabel(c.store_id, c.store_name) })}
          empty={(
            <EmptyState
              title={t('superadminOps:platforms.empty')}
              hint={filtersActive ? t('superadminOps:platforms.emptyFiltered') : t('superadminOps:platforms.emptyHint')}
            />
          )}
          testId="sa-platforms-table"
        />
      )}

      <DetailPanel
        open={panelOpen && formData !== null}
        onClose={requestClose}
        title={isEdit ? t('superadminOps:platforms.editTitle') : t('superadminOps:platforms.newTitle')}
        subtitle={isEdit && editingCred
          ? `${platformLabel(editingCred.platform_name)} · ${storeLabel(editingCred.store_id, editingCred.store_name)}`
          : t('superadminOps:platforms.newHint')}
        footerStart={isEdit ? (
          <button type="button" className={DANGER_GHOST_BTN} onClick={() => setConfirm('delete')} disabled={saving}>
            <Trash2 className="w-4 h-4" aria-hidden="true" />
            {t('superadminOps:delete')}
          </button>
        ) : undefined}
        footer={(
          <>
            <button type="button" className={GHOST_BTN} onClick={requestClose} disabled={saving}>{t('common:cancel')}</button>
            <button type="submit" form={FORM_ID} className={PRIMARY_BTN} disabled={saving} aria-busy={saving || undefined}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {saving ? t('admin:saving') : t('admin:save')}
            </button>
          </>
        )}
        testId="sa-platform-panel"
      >
        {formData && (
          <form id={FORM_ID} onSubmit={handleSave} noValidate className="space-y-5">
            <div className="xs-grid2">
              {isEdit ? (
                <SheetLocked label={t('admin:storeSection')} value={storeLabel(formData.store_id, editingCred?.store_name)} />
              ) : (
                <SheetField label={`${t('admin:storeSection')} *`} htmlFor="sa-platform-store">
                  <select
                    id="sa-platform-store"
                    value={formData.store_id}
                    onChange={e => change('store_id', Number(e.target.value))}
                    className={SELECT_CLS}
                    aria-invalid={storeMissing || undefined}
                    aria-describedby={storeMissing ? 'sa-platform-store-error' : undefined}
                  >
                    {stores.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.display_name || s.name} (#{s.id}){s.organization_name ? ` · ${s.organization_name}` : ''}
                      </option>
                    ))}
                  </select>
                  {storeMissing && (
                    <p id="sa-platform-store-error" role="alert" className="mt-1.5 text-xs text-red-600 dark:text-red-400">{t('admin:selectStore')}</p>
                  )}
                </SheetField>
              )}
              {isEdit ? (
                <SheetLocked label={t('admin:platformLabel')} value={platformLabel(formData.platform_name)} />
              ) : (
                <SheetField label={`${t('admin:platformLabel')} *`} htmlFor="sa-platform-name">
                  <select
                    id="sa-platform-name"
                    value={formData.platform_name}
                    onChange={e => change('platform_name', e.target.value)}
                    className={SELECT_CLS}
                  >
                    {PLATFORMS.map(p => <option key={p} value={p}>{platformLabel(p)}</option>)}
                  </select>
                </SheetField>
              )}
            </div>

            <div className="xs-grid2">
              <SheetField label={t('superadminOps:platforms.form.username')} htmlFor="sa-platform-username">
                <input id="sa-platform-username" value={formData.api_username} onChange={e => change('api_username', e.target.value)}
                  className={INPUT_CLS} autoComplete="one-time-code" spellCheck={false} />
              </SheetField>
              <SheetField
                label={t('superadminOps:platforms.form.password')}
                htmlFor="sa-platform-password"
                hint={isEdit ? t('superadminOps:platforms.form.keepHint') : undefined}
              >
                <input id="sa-platform-password" type="password" value={formData.api_password} onChange={e => change('api_password', e.target.value)}
                  className={INPUT_CLS} autoComplete="one-time-code" aria-describedby={isEdit ? 'sa-platform-password-hint' : undefined} />
              </SheetField>
            </div>

            <div className="xs-grid2">
              <SheetField
                label={t('superadminOps:platforms.form.apiKey')}
                htmlFor="sa-platform-key"
                hint={isEdit ? t('superadminOps:platforms.form.keepHint') : undefined}
              >
                <input id="sa-platform-key" value={formData.api_key} onChange={e => change('api_key', e.target.value)}
                  className={INPUT_CLS} autoComplete="one-time-code" spellCheck={false} aria-describedby={isEdit ? 'sa-platform-key-hint' : undefined} />
              </SheetField>
              <SheetField label={t('superadminOps:platforms.form.restaurant')} htmlFor="sa-platform-restaurant">
                <input id="sa-platform-restaurant" value={formData.restaurant_id} onChange={e => change('restaurant_id', e.target.value)}
                  className={`${INPUT_CLS} font-mono`} spellCheck={false} />
              </SheetField>
            </div>

            <div className="pt-1">
              <SwitchRow
                title={t('superadminOps:platforms.form.autoAccept')}
                subtitle={t('superadminOps:platforms.form.autoAcceptHint')}
                checked={formData.auto_accept_orders}
                onChange={v => change('auto_accept_orders', v)}
              />
              <SwitchRow
                title={t('superadminOps:platforms.form.selfDelivery')}
                subtitle={t('superadminOps:platforms.form.selfDeliveryHint')}
                checked={formData.is_self_delivery}
                onChange={v => change('is_self_delivery', v)}
              />
            </div>

            <SheetField label={t('superadminOps:platforms.form.time')} htmlFor="sa-platform-time" className="sm:max-w-[14rem]">
              <input id="sa-platform-time" type="number" value={formData.est_delivery_time}
                onChange={e => change('est_delivery_time', Number(e.target.value))}
                className={`${INPUT_CLS} tabular-nums`} min={5} max={180} />
            </SheetField>

            {formError && <p role="alert" className="text-sm font-medium text-red-600 dark:text-red-400">{formError}</p>}
          </form>
        )}
        {confirmModal}
      </DetailPanel>
    </ListPage>
  );
};

export default SuperAdminPlatformsTab;
